if (!customElements.get('product-merchandising')) {
  class ProductMerchandising extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'evangeline:recent-products:v1';
      this.maximumStoredProducts = 12;
      this.maximumRenderedProducts = Number(this.dataset.productsToShow || 6);
      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.relatedTab = this.querySelector('[data-related-tab]');
      this.recentTab = this.querySelector('[data-recent-tab]');
      this.recentList = this.querySelector('[data-recent-list]');
      this.previousHandles = this.readStoredHandles().filter((handle) => handle !== this.dataset.currentHandle);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      this.writeStoredHandles([
        this.dataset.currentHandle,
        ...this.previousHandles,
      ].filter(Boolean).slice(0, this.maximumStoredProducts));

      if (this.previousHandles.length > 0) this.observeRecentProducts();
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });
      this.observer?.disconnect();
    }

    readStoredHandles() {
      try {
        const value = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(value)) return [];
        return [...new Set(value.filter((handle) => typeof handle === 'string' && handle.length > 0))];
      } catch (_error) {
        return [];
      }
    }

    writeStoredHandles(handles) {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(handles));
      } catch (_error) {
        // Storage can be unavailable in private or restricted browsing contexts.
      }
    }

    observeRecentProducts() {
      if (!('IntersectionObserver' in window)) {
        this.loadRecentProducts();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.loadRecentProducts();
        },
        { rootMargin: '0px 0px 500px 0px' }
      );
      this.observer.observe(this);
    }

    async loadRecentProducts() {
      if (this.recentProductsLoaded || !this.recentList) return;
      this.recentProductsLoaded = true;

      const handles = this.previousHandles.slice(0, this.maximumRenderedProducts);
      const responses = await Promise.allSettled(handles.map((handle) => this.fetchCard(handle)));
      const cards = responses
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map((result) => result.value);

      if (cards.length === 0) return;

      this.recentList.replaceChildren(...cards);
      this.recentTab.hidden = false;
    }

    async fetchCard(handle) {
      const root = this.dataset.routesRoot || '/';
      const requestUrl = `${root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(
        this.dataset.recentCardSectionId
      )}`;
      const response = await fetch(requestUrl, {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) return null;

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = documentFragment.querySelector('[data-recent-product-card]');
      if (!card || card.dataset.productHandle === this.dataset.currentHandle) return null;
      return card;
    }

    onTabClick(event) {
      this.activateTab(event.currentTarget);
    }

    onTabKeydown(event) {
      const visibleTabs = this.tabs.filter((tab) => !tab.hidden);
      const currentIndex = visibleTabs.indexOf(event.currentTarget);
      let targetIndex;

      if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % visibleTabs.length;
      if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = visibleTabs.length - 1;
      if (targetIndex === undefined) return;

      event.preventDefault();
      visibleTabs[targetIndex].focus();
      this.activateTab(visibleTabs[targetIndex]);
    }

    activateTab(activeTab) {
      this.tabs.forEach((tab) => {
        const selected = tab === activeTab;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        if (panel) panel.hidden = !selected;
      });
    }
  }

  customElements.define('product-merchandising', ProductMerchandising);
}
